import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle, AlertTriangle, Clock, Trash2, RefreshCw } from 'lucide-react';
import { platformAPI } from '../../services/api';
import toast from 'react-hot-toast';

const TYPE_CONFIG = {
  subscription: { icon: Clock, color: '#3b82f6', bg: '#eff6ff' },
  payment: { icon: CheckCircle, color: '#16a34a', bg: '#f0fdf4' },
  alert: { icon: AlertTriangle, color: '#f59e0b', bg: '#fffbeb' },
  info: { icon: Bell, color: '#6366f1', bg: '#eef2ff' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function SuperAdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await platformAPI.getNotifications();
      setNotifications(data);
    } catch (err) {
      // Table might not exist yet
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const markRead = async (id) => {
    try {
      await platformAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllRead = async () => {
    try {
      await platformAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div>
      <div className="sa-toolbar">
        <div className="sa-toolbar-left">
          <span className="sa-toolbar-title">{unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sa-btn sa-btn-outline" onClick={fetchNotifications} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
          {unreadCount > 0 && (
            <button className="sa-btn sa-btn-outline" onClick={markAllRead}>Mark All as Read</button>
          )}
        </div>
      </div>

      <div className="sa-card">
        <div style={{ padding: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              <p style={{ margin: 0 }}>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <Bell size={36} style={{ margin: '0 auto 12px', display: 'block' }} />
              <p style={{ margin: 0 }}>No notifications yet</p>
              <p style={{ margin: '4px 0 0', fontSize: 12 }}>Notifications will appear here when companies register, subscriptions change, or payments occur.</p>
            </div>
          ) : notifications.map(n => {
            const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.info;
            const Icon = cfg.icon;
            return (
              <div key={n.id} onClick={() => markRead(n.id)} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '14px 20px',
                borderBottom: '1px solid #f1f5f9',
                background: n.is_read ? '#fff' : '#f8fafc',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={14} style={{ color: cfg.color }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700, color: '#1e293b' }}>{n.title}</span>
                    {!n.is_read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />}
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{n.message}</p>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, display: 'block' }}>{timeAgo(n.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
